import React, { useState } from "react";
import axios from "axios";
import { Plus, Pencil, Trash2, X, RotateCcw } from "lucide-react";
import formatApiError from "../../lib/apiError";
import useActiveInactive from "../../lib/useActiveInactive";
import ActiveInactiveTabs from "../../components/ActiveInactiveTabs";

const API = process.env.REACT_APP_BACKEND_URL;
const ax = () => ({ withCredentials: true });
const BASE = `${API}/api/subjects`;

export default function SubjectsPage() {
  const { view, setView, list: subjects, inactiveCount, loading, error, reload } = useActiveInactive(BASE);

  const [modal, setModal]         = useState(null);
  const [form, setForm]           = useState({ code: "", name: "", description: "" });
  const [formErr, setFormErr]     = useState("");
  const [saving, setSaving]       = useState(false);

  const [confirmId, setConfirmId] = useState(null);
  const [deleting, setDeleting]   = useState(false);
  const [restoringId, setRestoringId] = useState(null);

  const openAdd = () => { setForm({ code: "", name: "", description: "" }); setFormErr(""); setModal({ mode: "add", subject: null }); };
  const openEdit = (s) => { setForm({ code: s.code, name: s.name, description: s.description }); setFormErr(""); setModal({ mode: "edit", subject: s }); };
  const closeModal = () => { setModal(null); setFormErr(""); };

  const handleSave = async () => {
    if (!form.name.trim()) { setFormErr("Name is required."); return; }
    if (!form.code.trim()) { setFormErr("Code is required."); return; }
    setSaving(true); setFormErr("");
    try {
      if (modal.mode === "add") await axios.post(BASE, form, ax());
      else                       await axios.put(`${BASE}/${modal.subject.id}`, form, ax());
      closeModal(); reload();
    } catch (e) { setFormErr(formatApiError(e, "Something went wrong.")); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try { await axios.delete(`${BASE}/${confirmId}`, ax()); setConfirmId(null); reload(); }
    catch { setConfirmId(null); }
    finally { setDeleting(false); }
  };

  const handleRestore = async (id) => {
    setRestoringId(id);
    try { await axios.post(`${BASE}/${id}/restore`, {}, ax()); reload(); }
    catch (e) { alert(formatApiError(e, "Could not restore.")); }
    finally { setRestoringId(null); }
  };

  return (
    <div className="page-wrap">
      <div className="page-header-row">
        <div>
          <h1 className="page-title">Subjects</h1>
          <p className="page-sub">Manage all medical subjects.</p>
        </div>
        {view === "active" && (
          <button className="btn-primary" onClick={openAdd} data-testid="add-subject-btn">
            <Plus size={16} /> Add Subject
          </button>
        )}
      </div>

      <ActiveInactiveTabs view={view} onChange={setView} inactiveCount={inactiveCount} testIdPrefix="subjects-view" />

      {loading ? <div className="table-loading">Loading…</div>
       : error ? <div className="table-error">{error}</div>
       : (
        <div className="data-card">
          {subjects.length === 0 ? (
            <div className="empty-state">
              {view === "active"
                ? <p>No subjects yet. Click <strong>Add Subject</strong> to create one.</p>
                : <p>No inactive subjects.</p>}
            </div>
          ) : (
            <table className="data-table" data-testid="subjects-table">
              <thead>
                <tr>
                  <th style={{ width: 48 }}>#</th>
                  <th style={{ width: 110 }}>Code</th>
                  <th>Name</th>
                  <th>Description</th>
                  <th style={{ width: 110 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {subjects.map((s, i) => (
                  <tr key={s.id} data-testid={`subject-row-${s.id}`}>
                    <td className="td-num">{i + 1}</td>
                    <td><span className="td-badge">{s.code}</span></td>
                    <td className="td-name">{s.name}</td>
                    <td className="td-desc">{s.description || <span className="td-empty">—</span>}</td>
                    <td>
                      <div className="action-btns">
                        {view === "active" ? (
                          <>
                            <button className="action-edit"   onClick={() => openEdit(s)}        data-testid={`edit-subject-${s.id}`}   title="Edit"><Pencil size={15} /></button>
                            <button className="action-delete" onClick={() => setConfirmId(s.id)} data-testid={`delete-subject-${s.id}`} title="Delete"><Trash2 size={15} /></button>
                          </>
                        ) : (
                          <button className="action-restore" onClick={() => handleRestore(s.id)} disabled={restoringId === s.id} data-testid={`restore-subject-${s.id}`} title="Restore"><RotateCcw size={15} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {modal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()} data-testid="subject-modal">
            <div className="modal-head">
              <h2 className="modal-title">{modal.mode === "add" ? "Add Subject" : "Edit Subject"}</h2>
              <button className="modal-close" onClick={closeModal} data-testid="modal-close-btn"><X size={18} /></button>
            </div>
            {formErr && <div className="form-error" data-testid="form-error">{formErr}</div>}
            <div className="modal-body">
              <div className="field-row">
                <div className="field">
                  <label className="field-label">Code <span className="required">*</span></label>
                  <input className="field-input" placeholder="e.g. ANAT" value={form.code}
                    onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                    data-testid="subject-code-input" maxLength={20} style={{ textTransform: "uppercase" }} />
                </div>
                <div className="field" style={{ flex: 2 }}>
                  <label className="field-label">Name <span className="required">*</span></label>
                  <input className="field-input" placeholder="e.g. Anatomy" value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    data-testid="subject-name-input" maxLength={120} autoFocus />
                </div>
              </div>
              <div className="field">
                <label className="field-label">Description</label>
                <textarea className="field-input field-textarea" placeholder="Brief description of the subject…"
                  value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  data-testid="subject-desc-input" maxLength={600} rows={3} />
              </div>
            </div>
            <div className="modal-foot">
              <button className="btn-ghost" onClick={closeModal} data-testid="cancel-btn">Cancel</button>
              <button className="btn-primary" onClick={handleSave} disabled={saving} data-testid="save-subject-btn">
                {saving ? <span className="btn-spinner" /> : modal.mode === "add" ? "Add Subject" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmId && (
        <div className="modal-overlay" onClick={() => setConfirmId(null)}>
          <div className="modal-box modal-box-sm" onClick={(e) => e.stopPropagation()} data-testid="delete-confirm-modal">
            <div className="modal-head">
              <h2 className="modal-title">Delete Subject</h2>
              <button className="modal-close" onClick={() => setConfirmId(null)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <p className="confirm-text">Move this subject to <strong>Inactive</strong>? It will be hidden from selection lists but can be restored later.</p>
            </div>
            <div className="modal-foot">
              <button className="btn-ghost" onClick={() => setConfirmId(null)}>Cancel</button>
              <button className="btn-danger" onClick={handleDelete} disabled={deleting} data-testid="confirm-delete-btn">
                {deleting ? <span className="btn-spinner" /> : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
